import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { Loader2, Package, Search, X, RefreshCw, ArrowLeft } from 'lucide-react';
import ParsedOrderViewModal from './ParsedOrderViewModal';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) { return iso; }
};

const orderTotal = (o) => (o.product_lines || []).reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0);

/**
 * Orders tab of the Customer Management side panel — this customer's parsed orders as oblong cards.
 * Clicking an order replaces the list with that order's full details (the same order view Transport shows) across the WHOLE
 * panel, with a Back button at the top to return to the orders list.
 *
 *   focusOrderId     (optional) the id of one order to open straight away once the list has loaded — used when Customer
 *                    Management is opened from a search for an order number.
 *   onFocusConsumed  (optional) called once that order has been opened, so the parent stops asking for it.
 */
export default function CustomerOrdersPanel({ account, focusOrderId = null, onFocusConsumed = null, orderNumberTabs = null }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [switchingTo, setSwitchingTo] = useState(null);

  // Opened from a Transport entry (orderNumberTabs is set): jump between the order numbers on that same delivery.
  const switchToOrder = async (num) => {
    const clean = String(num).trim().replace(/^#/, '');
    const inList = orders.find((o) => String(o.order_number || '').trim().replace(/^#/, '') === clean);
    if (inList) { setSelected(inList); return; }
    setSwitchingTo(num);
    try {
      const res = await api.get(`${API}/automations/orders/${encodeURIComponent(clean)}`);
      setSelected(res.data);
    } catch (_) {
      toast.error(`Could not open order #${clean}`);
    } finally {
      setSwitchingTo(null);
    }
  };

  const load = async () => {
    if (!account?.id && !account?.name) return;
    setLoading(true);
    setError('');
    try {
      const params = { account_id: account.id || '', name: account.name || '' };
      if (focusOrderId) params.focus_order_id = focusOrderId;
      const res = await api.get(`${API}/customer-pricing/orders`, { params });
      const list = res.data?.orders || [];
      setOrders(list);
      if (focusOrderId) {
        const hit = list.find((o) => o.id === focusOrderId);
        if (hit) setSelected(hit);
        onFocusConsumed?.();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setSelected(null); load(); }, [account?.id, account?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const hay = [o.order_number, o.reference, o.delivery_postcode, o.invoice_customer_name, o.customer_name].join(' ').toLowerCase();
      if (hay.includes(q)) return true;
      return (o.product_lines || []).some((l) => `${l.product_code || ''} ${l.description || ''}`.toLowerCase().includes(q));
    });
  }, [orders, query]);

  return (
    <div className="h-full flex flex-col" data-testid="customer-orders-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
        >
          <Package className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight">Orders</h3>
          <p className="text-[11px] text-gray-500 truncate">{account?.name} — parsed sales orders</p>
        </div>
        <button onClick={load} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {selected ? (
        /* Order details — the whole panel, with a Back button to the orders list */
        <div className="flex-1 min-h-0 flex flex-col" data-testid="customer-order-detail">
          {orderNumberTabs && orderNumberTabs.length > 1 && (
            <div className="flex items-center gap-1.5 px-4 pt-2 pb-2 border-b border-orange-100 flex-shrink-0 overflow-x-auto" data-testid="customer-order-tabs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0 mr-1">This delivery</span>
              {orderNumberTabs.map((num) => {
                const active = String(selected.order_number || '').trim().replace(/^#/, '') === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => switchToOrder(num)}
                    disabled={switchingTo === num}
                    className={`rounded-full px-3 py-1 text-xs font-bold border-2 whitespace-nowrap transition-colors ${active ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'}`}
                    style={active ? { backgroundColor: ORANGE } : undefined}
                    data-testid={`customer-order-tab-${num}`}
                  >
                    #{num}{switchingTo === num ? ' …' : ''}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 flex-shrink-0">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-1.5 rounded-xl h-8 px-3 border-2 border-orange-300 text-orange-700 bg-white text-xs font-bold hover:bg-orange-50 transition-colors"
              data-testid="customer-order-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Orders
            </button>
            <span className="text-sm font-bold text-gray-900 font-mono">#{selected.order_number || '—'}</span>
            {selected.is_direct_delivery && (
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-blue-100 text-blue-700" data-testid="customer-order-direct-badge">
                Direct
              </span>
            )}
          </div>
          <div className="flex-1 min-h-0">
            <ParsedOrderViewModal order={selected} inline={true} onClose={() => setSelected(null)} />
          </div>
        </div>
      ) : (
        /* Order list — full width */
        <div className="flex-1 min-h-0 flex flex-col p-4">
          <div className="relative mb-3 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search order number, reference, postcode, product code / description…"
              data-testid="customer-orders-search"
              className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 placeholder-gray-300 focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-200 bg-gray-50"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading orders…</span>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-sm text-red-600">{error}</div>
            ) : visible.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                <p className="text-sm">{query ? `No orders match "${query}"` : 'No orders found for this customer'}</p>
              </div>
            ) : (
              visible.map((o) => {
                const total = orderTotal(o);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setSelected(o)}
                    className="w-full text-left flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 transition-all hover:border-orange-300 hover:bg-orange-50/40"
                    data-testid={`customer-order-${o.order_number}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fff3e0' }}>
                      <Package className="w-4 h-4" style={{ color: ORANGE }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900 font-mono">#{o.order_number || '—'}</span>
                        {o.is_direct_delivery && (
                          <span className="text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-blue-100 text-blue-700">Direct</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {[o.reference ? `Ref: ${o.reference}` : null, o.delivery_postcode, `${(o.product_lines || []).length} line${(o.product_lines || []).length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-gray-800 tabular-nums">
                        {total > 0 ? `£${total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                      </div>
                      <div className="text-[10px] text-gray-400">{fmtDate(o.received_at)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
